import { configureAuth } from 'react-query-auth'
import { z } from 'zod'

import { queryOptions, useMutation, useQuery } from '@tanstack/react-query'
import { Navigate } from 'react-router-dom'
import { FullContainerSpin } from '../components/spin'
import { api, loginApi, userApi } from './api-client'
import type { MutationConfig } from './react-query'

// api call definitions for auth (types, schemas, requests):
// these are not part of features as this is a module shared across features
export interface User {
  id: number
  name: string
  workspace_id: number
  role_id: number
  display_field_orders: string[]
  email: string
  tmp_email: string
  is_notifiable: 0 | 1
  old_passwords: string | null
  created_at: string
  updated_at: string
  // TODO: Lead周りの型定義が必要
  add_lead_settings: any
  lead_detail_display_settings: any
  filtering_conditions: any
}

const getUser = async (): Promise<User> => {
  return await userApi.get('me')
}

const logout = (): Promise<void> => {
  return api.post('/auth/logout')
}

export const loginInputSchema = z.object({
  email: z
    .string()
    .min(1, 'メールアドレスは必須項目です')
    .email('メールアドレスの形式が正しくありません'),
  password: z
    .string()
    .min(8, 'パスワードは8文字以上で入力してください')
    .max(100, 'パスワードは100文字以下で入力してください')
    .regex(
      /^[a-zA-Z\d^$*.\[\]{}()?\"!@#%&\/\\,><':;|_~`\-\+]+$/,
      '小文字,数字,記号含めて入力してください',
    ),
})

export type LoginInput = z.infer<typeof loginInputSchema>

interface AuthResponse {
  data: {
    access_token: string
    expires_in: number
    refresh_token: string
    token_type: string
  }
}

const loginWithEmailAndPassword = (data: LoginInput): Promise<AuthResponse> => {
  return loginApi.post('/oauth/token', {
    username: data.email,
    password: data.password,
    redirect_uri: 'http://localhost',
    grant_type: 'password',
    // @ts-ignore
    client_id: window.CLIENT_ID,
    // @ts-ignore
    client_secret: window.CLIENT_SECRET,
    provider: 'cms_admins',
  })
}

export const passwordResetSchema = z.object({
  email: z
    .string()
    .min(1, 'メールアドレスは必須項目です')
    .email('メールアドレスの形式が正しくありません'),
})

export type PasswordResetInput = z.infer<typeof passwordResetSchema>

const resetPassword = (data: PasswordResetInput): Promise<void> => {
  return loginApi.post('api/cms_admin/password/email', data)
}

type useResetPasswordMutationOptions = {
  mutationConfig?: MutationConfig<typeof resetPassword>
}

export const useResetPassword = ({
  mutationConfig,
}: useResetPasswordMutationOptions = {}) => {
  return useMutation({
    mutationFn: resetPassword,
    ...mutationConfig,
  })
}

const editPassword = (
  id: string,
): Promise<{
  id: string
  email: string
}> => {
  return api.get(`/cms_admin/password/reset/${id}`)
}

type UseEditPasswordOptions = {
  id: string
  queryConfig?: MutationConfig<typeof editPassword>
}

const editPasswordQueryOptions = (id: string) => {
  return queryOptions({
    queryKey: ['password'],
    queryFn: () => editPassword(id),
  })
}

export const useEditPassword = ({
  id,
  queryConfig,
}: UseEditPasswordOptions) => {
  return useQuery({
    ...editPasswordQueryOptions(id),
    ...queryConfig,
  })
}

export const updatePasswordSchema = z.object({
  id: z.string(),
  email: z
    .string()
    .min(1, 'メールアドレスは必須項目です')
    .email('メールアドレスの形式が正しくありません'),
  password: z
    .string()
    .min(8, 'パスワードは8文字以上で入力してください')
    .max(100, 'パスワードは100文字以下で入力してください')
    .regex(
      /^[a-zA-Z\d^$*.\[\]{}()?\"!@#%&\/\\,><':;|_~`\-\+]+$/,
      '小文字,数字,記号含めて入力してください',
    ),
})

export type UpdatePasswordInput = z.infer<typeof updatePasswordSchema>

const updatePassword = (data: UpdatePasswordInput): Promise<User> => {
  return loginApi.post('/api/cms_admin/password/reset', {
    grant_type: 'password',
    // @ts-ignore
    client_id: window.CLIENT_ID,
    // @ts-ignore
    client_secret: window.CLIENT_SECRET,
    provider: 'cms_admins',
    ...data,
  })
}

type useUpdatePasswordMutationOptions = {
  mutationConfig?: MutationConfig<typeof updatePassword>
}

export const useUpdatePassword = ({
  mutationConfig,
}: useUpdatePasswordMutationOptions = {}) => {
  const { onSuccess, ...restConfig } = mutationConfig || {}

  return useMutation({
    onSuccess: async (data, variables, ...args) => {
      localStorage.removeItem('authToken')
      const loginResponse = await loginWithEmailAndPassword({
        email: data.email,
        password: variables.password,
      })
      localStorage.setItem('authToken', JSON.stringify(loginResponse))
      onSuccess?.(data, variables, ...args)
    },
    ...restConfig,
    mutationFn: updatePassword,
  })
}

const showInvite = (
  id: string,
): Promise<{
  id: string
  email: string
}> => {
  return api.get(`/v3/common/auth/invite/${id}`)
}

type UseShowInviteOptions = {
  id: string
  queryConfig?: MutationConfig<typeof editPassword>
}

const showInviteQueryOptions = (id: string) => {
  return queryOptions({
    queryKey: ['invite', { id }],
    queryFn: () => showInvite(id),
  })
}

export const useShowInvite = ({ id, queryConfig }: UseShowInviteOptions) => {
  return useQuery({
    ...showInviteQueryOptions(id),
    ...queryConfig,
  })
}

export const signUpInputSchema = z.object({
  id: z.string().min(1),
  email: z
    .string()
    .min(1, 'メールアドレスは必須項目です')
    .email('メールアドレスの形式が正しくありません'),
  name: z
    .string()
    .min(1, '名前は必須項目です')
    .max(255, '名前は最大255文字です'),
  password: z
    .string()
    .min(8, 'パスワードは8文字以上で入力してください')
    .max(100, 'パスワードは100文字以下で入力してください')
    .regex(
      /^[a-zA-Z\d^$*.\[\]{}()?\"!@#%&\/\\,><':;|_~`\-\+]+$/,
      '小文字,数字,記号含めて入力してください',
    ),
})

export type SignUpInput = z.infer<typeof signUpInputSchema>

const signUp = (
  data: SignUpInput,
): Promise<{
  id: string
  name: string
  email: string
}> => {
  return api.post('/v3/common/auth/invite', data)
}

const authConfig = {
  userFn: getUser,
  loginFn: async (data: LoginInput) => {
    localStorage.removeItem('authToken')
    const response = await loginWithEmailAndPassword(data)
    localStorage.setItem('authToken', JSON.stringify(response))
    const user = await getUser()
    return user
  },
  registerFn: async (data: SignUpInput) => {
    localStorage.removeItem('authToken')
    const response = await signUp(data)
    const loginResponse = await loginWithEmailAndPassword({
      email: response.email,
      password: data.password,
    })
    localStorage.setItem('authToken', JSON.stringify(loginResponse))
    const user = await getUser()
    return user
  },
  logoutFn: logout,
}

export const { useUser, useLogin, useLogout, useRegister, AuthLoader } =
  configureAuth(authConfig)

export const ProtectedRoute = ({ children }: { children: JSX.Element }) => {
  const user = useUser({ staleTime: 1000 * 60 * 60 })

  if (user.isFetching) {
    return <FullContainerSpin />
  }

  if (!user.data) {
    return <Navigate to="/login" replace />
  }

  return children
}
